/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  ComponentType,
  SVGProps,
  ReactNode,
} from 'react';
import { useHistory, useLocation, Prompt } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import {
  IconSparkles,
  IconDatabase,
  IconShieldCheck,
  IconEye,
  IconDownload,
  IconCheck,
  IconAlertTriangle,
  IconReceipt,
  IconTable,
} from '../credit-plans/SVGIcons';
import './reportBuilder.css';
import { GlobalContext } from 'providers/GlobalProvider';
import ReportsAPI from 'api/ReportsAPI';
import DocumentLibraryAPI from 'api/DocumentLibraryAPI';
import ReactMarkdown from 'react-markdown';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import ToolBreadcrumb, {
  IToolBreadcrumbItem,
} from 'components/Common/ToolBreadcrumb';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';
import {
  COMPARISON_DUTY_COLUMNS,
  COMPARISON_METRIC_COLUMNS,
  comparisonCellText,
  computeComparisonTotals,
  formatCellDisplay,
  formatCurrency,
  humanizeHeader,
  isIdentifierColumn,
} from './reportFormat';
import {
  WorkflowAssistantHeader,
  WorkflowComposer,
  WorkflowNotice,
  WorkflowPageHeader,
  WorkflowProgressHeader,
  useScrollWorkflowIntoView,
} from 'components/Workflows/StandardWorkflowLayout';

// Compact markdown overrides for chat bubbles — keeps the agent's light
// formatting (bold, lists, the rare paragraph break) at the chat font size
// instead of rendering raw asterisks. No remark-gfm: the agent is instructed
// not to emit tables, so plain markdown is sufficient.
const CHAT_MD_COMPONENTS = {
  p: ({ children }: any) => <p className='rb-md-p'>{children}</p>,
  ul: ({ children }: any) => <ul className='rb-md-list'>{children}</ul>,
  ol: ({ children }: any) => <ol className='rb-md-list'>{children}</ol>,
  li: ({ children }: any) => <li>{children}</li>,
  strong: ({ children }: any) => <strong>{children}</strong>,
  a: ({ href, children }: any) => (
    <a href={href} target='_blank' rel='noreferrer'>
      {children}
    </a>
  ),
};

const SUGGESTED_PROMPTS = [
  'Compare Section 232 duty spend by country: ACE vs Tariff',
  'Tariff exposure by SKU',
  'Calculate total Section 232 & IEEPA duties',
  'ACE discrepancy report',
  'Product landed cost by category',
  // 'Product compliance status by category',
  // 'Supplier risk summary',
  'Duty spend by country',
];

type StepId =
  | 'goal'
  | 'sources'
  | 'fields'
  | 'filters'
  | 'validate'
  | 'preview';
type StepStatus = 'pending' | 'active' | 'done';

interface StepDef {
  id: StepId;
  title: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  summary: string;
}

const STEPS: StepDef[] = [
  {
    id: 'goal',
    title: 'Define Report Goal',
    icon: IconSparkles,
    summary:
      "Captured the user's natural-language reporting goal and reformulated it as a structured business question.",
  },
  {
    id: 'sources',
    title: 'Data Sources',
    icon: IconDatabase,
    summary:
      'Identified governed data sources required to answer the question.',
  },
  {
    id: 'preview',
    title: 'Preview & Export',
    icon: IconEye,
    summary: 'Generated a sampled preview and prepared a CSV export.',
  },
];

// Route state shape sent by the "Build Report" buttons on AceRunDetailsPage,
// TariffRunDetailsPage, and the BITE DB database page (DatabaseInfo.tsx).
interface SourceRun {
  type: 'ace' | 'tariff' | 'bitedb';
  runId: string;
  label: string;
}

// Breadcrumb root item + detail-page path per source, keyed by SourceRun.type.
const SOURCE_RUN_ROOT: Record<SourceRun['type'], IToolBreadcrumbItem> = {
  ace: { label: 'ACE Analyzer', to: '/ace-analyzers' },
  tariff: { label: 'Tariff Analyzer', to: '/tariff-analyzers' },
  bitedb: { label: 'BITE DB', to: '/bite-db/entities' },
};
const SOURCE_RUN_DETAIL_PATH: Record<SourceRun['type'], (id: string) => string> =
  {
    ace: (id) => `/ace-analyzers/${id}`,
    tariff: (id) => `/tariff-analyzers/${id}`,
    bitedb: (id) => `/database/${id}`,
  };

interface DataSource {
  name: string;
  meta: string;
}
interface ReportPlan {
  name: string;
  question: string;
  sources: DataSource[];
  columns: string[];
  filters: string[];
  groupings: string[];
  metrics: string[];
  warnings: string[];
  assumptions: string[];
  insights: string[];
  // 'comparison' switches the preview to the dedicated ACE-vs-TARIFF delta layout.
  mode?: 'comparison';
  // Resolved tariff-program scope echoed by the backend ('total' | 'sec_232' | …)
  // and its display label ('Total' | 'Section 232' | …). Drives the program chips.
  program?: string;
  programLabel?: string;
}

interface Attachment {
  category: string;
  categoryLabel: string;
  id: string;
  file: string;
}

const CSV_FORMULA_PREFIX = /^[=+\-@\t\r]/;

const escapeCsvCell = (value: unknown): string => {
  let text = value === null || value === undefined ? '' : String(value);
  if (CSV_FORMULA_PREFIX.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

const buildReportCsvFile = (args: {
  name: string;
  columns: string[];
  rows: any[];
  mode?: 'comparison';
  sources: Attachment[];
}): { blob: Blob; fileName: string } => {
  const header = args.columns
    .map((column) => escapeCsvCell(humanizeHeader(column)))
    .join(',');
  const rows = args.rows.map((row: any) =>
    args.columns
      .map((column) => {
        let value = row[column];
        if (value === undefined || value === null) {
          value = row[column.toLowerCase().replace(/ /g, '_')];
        }
        return escapeCsvCell(comparisonCellText(args.mode, column, value));
      })
      .join(','),
  );

  let totalsLine: string | null = null;
  if (
    args.mode === 'comparison' &&
    args.columns.some((column) => !COMPARISON_METRIC_COLUMNS.has(column))
  ) {
    const historical = args.rows.reduce(
      (total, row) => total + (Number(row['Historical Duty']) || 0),
      0,
    );
    const projected = args.rows.reduce(
      (total, row) => total + (Number(row['Projected Duty']) || 0),
      0,
    );
    const percent =
      historical === 0
        ? null
        : Math.round(((projected - historical) / historical) * 1000) / 10;
    totalsLine = args.columns
      .map((column, index) => {
        let output = '';
        if (index === 0) output = 'Total';
        else if (column === 'Historical Duty') output = formatCurrency(historical);
        else if (column === 'Projected Duty') output = formatCurrency(projected);
        else if (column === 'Delta') output = formatCurrency(projected - historical);
        else if (column === 'Delta %') {
          output = percent === null ? '—' : `${percent > 0 ? '+' : ''}${percent}%`;
        }
        return escapeCsvCell(output);
      })
      .join(',');
  }

  const sourceLabel = args.sources
    .map((source) => `${source.categoryLabel}: ${source.file}`)
    .join(' · ');
  const csv = [
    header,
    ...rows,
    ...(totalsLine ? [totalsLine] : []),
    ...(sourceLabel
      ? [escapeCsvCell(`Source Dataset(s): ${sourceLabel}`)]
      : []),
  ].join('\n');
  const slugify = (value: string) =>
    value
      .replace(/[^a-z0-9]/gi, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .toLowerCase();
  const sourceSlug = slugify(args.sources.map((source) => source.file).join('_'));
  const base =
    [slugify(args.name), sourceSlug].filter(Boolean).join('_') || 'bite_report';

  return {
    blob: new Blob([csv], { type: 'text/csv' }),
    fileName: `${base}_${moment().format('YYYY-MM-DD_HHmmss')}.csv`,
  };
};

// Imperative handle exposed by ReportChatPanel via forwardRef
interface ChatPanelHandle {
  sendMessage: (text: string, syncDatasets?: Attachment[]) => void;
}

interface AIReportBuilderProps {
  // Renders inline inside a host page's own tab content (e.g. DatabaseInfo's
  // "Build Report" tab) instead of as a full route: drops the breadcrumb and
  // the full-viewport min-height, matching CustomsEntryAnalyzer's `embedded`.
  embedded?: boolean;
  // Source-run context supplied directly by an embedding host, bypassing
  // location.state (there's no navigation to carry it) — same shape as the
  // "Build Report" buttons on the ACE/Tariff run detail pages pass via
  // history.push state.
  sourceRunOverride?: SourceRun;
}

interface ReportLibrarySaveArgs {
  requestId: string;
  title: string;
  sources: Attachment[];
  metadata?: Record<string, unknown>;
  columns: string[];
  rows: any[];
  mode?: 'comparison';
}

interface ReportLibrarySaveResult {
  entryId: string | null;
  saved: boolean;
}

const AIReportBuilder: React.FC<AIReportBuilderProps> = ({
  embedded = false,
  sourceRunOverride,
}) => {
  const { cur_user, isWhiteLabel } = useContext(GlobalContext);
  const history = useHistory();
  const location = useLocation();
  const [plan, setPlan] = useState<ReportPlan | null>(null);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [openStep, setOpenStep] = useState<StepId | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [chatkitThinking, setChatkitThinking] = useState(false);
  const [chatkitError, setChatkitError] = useState<string | null>(null);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [libraryOutputSaved, setLibraryOutputSaved] = useState(false);
  const librarySaveRef = useRef<{
    requestId: string;
    promise: Promise<ReportLibrarySaveResult>;
  } | null>(null);
  const fallbackLibraryRequestIdRef = useRef<string | null>(null);
  const [proposedSources, setProposedSources] = useState<any[]>([]);
  const [selectedDiscoveredIds, setSelectedDiscoveredIds] = useState<string[]>(
    [],
  );
  const [discoveryFilterType, setDiscoveryFilterType] = useState<
    'ALL' | 'ACE' | 'TARIFF' | 'PRODUCT'
  >('ALL');
  const [discoverySearchTerm, setDiscoverySearchTerm] = useState<string>('');
  const [sessionResetNonce, setSessionResetNonce] = useState<number>(0);
  const previewTableRef = React.useRef<HTMLDivElement>(null);
  // Ref for programmatically sending messages into the chat panel (e.g. on dataset confirm)
  const chatPanelRef = React.useRef<ChatPanelHandle>(null);
  const [userPrompt, setUserPrompt] = useState<string>('');
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  // Leave-warning modal state — a styled confirm shown before discarding an
  // in-progress workflow or an unsaved generated report on in-app navigation.
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  // The original transition type (PUSH/REPLACE/POP) of the blocked navigation, so
  // it can be resumed faithfully rather than always re-pushed.
  const [pendingAction, setPendingAction] = useState<
    'PUSH' | 'REPLACE' | 'POP'
  >('PUSH');
  const allowNavigationRef = useRef(false);

  const selectedTeamId = cur_user?.selected_team?.id;
  const hasConversation = Boolean(activeThreadId);
  const workflowMainRef = useScrollWorkflowIntoView(hasConversation);

  // Human-readable list of the source dataset(s) this report is built from —
  // shown with the preview and embedded as a metadata line in the CSV export.
  const sourceDatasetsLabel = attachments
    .map((a) => `${a.categoryLabel}: ${a.file}`)
    .join(' · ');

  // Warn before leaving with an in-progress workflow or an unsaved generated report.
  const shouldWarnOnLeave = hasConversation || chatkitThinking || previewLoaded;

  // License gating — the AI Report Builder requires the Business tier (or higher),
  // matching the EARChatUI pattern. Without it the tool renders read-only with a
  // RequiredLicense bar at the top; Business+/white-label users get full access.
  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;
  const requiredTiers = ['business', 'enterprise'];
  const userHasAccess =
    requiredTiers.includes(cur_user?.license) ||
    Boolean(isWhiteLabel) ||
    cur_user?.bite_super_admin === true;

  // "Build Report" buttons on the ACE/Tariff run detail pages deep-link here
  // with the source run in route state instead of leaving the reporting
  // goal blank; the BITE DB database page instead renders this embedded and
  // passes the source run directly, since there's no navigation to carry
  // state through. Fires once, as if the user had typed/clicked a suggested
  // prompt, so the rest of the goal -> sources -> preview flow is untouched.
  const sourceRun =
    sourceRunOverride ??
    (location.state as { sourceRun?: SourceRun } | undefined)?.sourceRun;
  const sourceRunHandledRef = useRef(false);
  useEffect(() => {
    if (!sourceRun || sourceRunHandledRef.current || !userHasAccess) return;
    sourceRunHandledRef.current = true;
    const sourceLabel =
      sourceRun.type === 'ace'
        ? 'ACE run'
        : sourceRun.type === 'tariff'
        ? 'Tariff Analyzer run'
        : 'BITE DB database';
    chatPanelRef.current?.sendMessage(
      `Build a report from ${sourceLabel} "${sourceRun.label}".`,
    );
  }, [sourceRun, userHasAccess]);

  const removeAttachment = (a: Attachment) =>
    setAttachments((prev) =>
      prev.filter((x) => !(x.category === a.category && x.id === a.id)),
    );

  const handleResetChat = () => {
    setPlan(null);
    setPreviewRows([]);
    setPreviewLoaded(false);
    setActiveThreadId(null);
    setLibraryOutputSaved(false);
    librarySaveRef.current = null;
    fallbackLibraryRequestIdRef.current = null;
    setAttachments([]);
    setProposedSources([]);
    setUserPrompt('');
    setSessionResetNonce((prev) => prev + 1);
  };

  const registerReportInLibrary = useCallback(
    async (args: {
      requestId: string;
      title: string;
      sources: Attachment[];
      metadata?: Record<string, unknown>;
    }): Promise<string | null> => {
      setLibraryOutputSaved(false);
      if (!selectedTeamId || args.sources.length === 0) return null;
      try {
        const entry = await DocumentLibraryAPI.createReportEntry(
          selectedTeamId,
          {
            clientRequestId: args.requestId,
            title: args.title,
            sources: args.sources.map((source) => ({
              type: source.category,
              id: source.id,
            })),
            metadata: args.metadata,
          },
        );
        return entry.id;
      } catch (error) {
        console.warn('Unable to add report to Document Library.', error);
        return null;
      }
    },
    [selectedTeamId],
  );

  const persistReportOutput = useCallback(
    async (
      entryId: string,
      args: {
        title: string;
        columns: string[];
        rows: any[];
        mode?: 'comparison';
        sources: Attachment[];
      },
    ): Promise<boolean> => {
      if (!selectedTeamId || args.rows.length === 0) return false;
      const file = buildReportCsvFile({
        name: args.title,
        columns: args.columns,
        rows: args.rows,
        mode: args.mode,
        sources: args.sources,
      });
      try {
        await DocumentLibraryAPI.uploadReportOutput(
          selectedTeamId,
          entryId,
          file.fileName,
          file.blob,
        );
        setLibraryOutputSaved(true);
        return true;
      } catch (error) {
        console.warn('Unable to persist report CSV in Document Library.', error);
        setLibraryOutputSaved(false);
        return false;
      }
    },
    [selectedTeamId],
  );

  const ensureReportSaved = useCallback(
    (args: ReportLibrarySaveArgs): Promise<ReportLibrarySaveResult> => {
      const existing = librarySaveRef.current;
      if (existing?.requestId === args.requestId) return existing.promise;

      const promise = (async (): Promise<ReportLibrarySaveResult> => {
        const entryId = await registerReportInLibrary(args);
        const saved = entryId
          ? await persistReportOutput(entryId, args)
          : false;
        return { entryId: entryId, saved: saved };
      })();

      librarySaveRef.current = { requestId: args.requestId, promise: promise };
      promise.then(
        (result) => {
          if (!result.saved && librarySaveRef.current?.promise === promise) {
            librarySaveRef.current = null;
          }
        },
        () => {
          if (librarySaveRef.current?.promise === promise) {
            librarySaveRef.current = null;
          }
        },
      );
      return promise;
    },
    [persistReportOutput, registerReportInLibrary],
  );

  const recordCompletedReport = useCallback(
    async (args: ReportLibrarySaveArgs): Promise<string | null> => {
      const result = await ensureReportSaved(args);
      return result.entryId;
    },
    [ensureReportSaved],
  );

  // Forward-only status derivation. Each signal only advances within a session
  // (discovery → attachments → preview), so a step that reaches 'done' never
  // reverts — the tracker only ever moves forward.
  const stepStatuses: Record<StepId, StepStatus> = useMemo(() => {
    const hasDiscoveredSources = proposedSources.length > 0;
    const hasAttachments = attachments.length > 0;

    const goal: StepStatus =
      hasDiscoveredSources || hasAttachments || previewLoaded
        ? 'done'
        : hasConversation
        ? 'active'
        : 'pending';

    const sources: StepStatus = hasAttachments
      ? 'done'
      : hasDiscoveredSources
      ? 'active'
      : 'pending';

    const preview: StepStatus = previewLoaded
      ? 'done'
      : hasAttachments
      ? 'active'
      : 'pending';

    // fields/filters/validate are not rendered (STEPS has only goal/sources/preview);
    // included solely to satisfy the Record<StepId, StepStatus> type.
    return {
      goal: goal,
      sources: sources,
      fields: sources,
      filters: sources,
      validate: sources,
      preview: preview,
    };
  }, [
    hasConversation,
    previewLoaded,
    proposedSources.length,
    attachments.length,
  ]);

  const toggleStep = (id: StepId) =>
    setOpenStep((current) => (current === id ? null : id));

  useEffect(() => {
    if (proposedSources.length > 0 && attachments.length === 0) {
      setOpenStep('sources');
    } else if (previewLoaded) {
      setOpenStep('preview');
    }
  }, [attachments.length, previewLoaded, proposedSources.length]);

  const handleDownloadCSV = async () => {
    if (!plan || previewRows.length === 0) return;
    const file = buildReportCsvFile({
      name: plan.name,
      columns: plan.columns,
      rows: previewRows,
      mode: plan.mode,
      sources: attachments,
    });
    const url = URL.createObjectURL(file.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    if (libraryOutputSaved) return;

    let requestId = activeThreadId;
    if (!requestId) {
      if (!fallbackLibraryRequestIdRef.current) {
        fallbackLibraryRequestIdRef.current = `report_${Date.now()}`;
      }
      requestId = fallbackLibraryRequestIdRef.current;
    }
    const result = await ensureReportSaved({
      requestId: requestId,
      title: plan.name,
      sources: attachments,
      metadata: {
        mode: plan.mode || null,
        program: plan.program || null,
        rowCount: previewRows.length,
      },
      columns: plan.columns,
      rows: previewRows,
      mode: plan.mode,
    });
    if (!result.entryId) {
      enqueueSnackbar('The CSV downloaded, but it could not be saved to the Document Library.', {
        variant: 'warning',
      });
      return;
    }
    if (result.saved) {
      enqueueSnackbar('Report saved to the Document Library.', {
        variant: 'success',
      });
    } else {
      enqueueSnackbar('The CSV downloaded, but it could not be saved to the Document Library.', {
        variant: 'warning',
      });
    }
  };

  const getStepSummary = (
    id: StepId,
    currentPlan: ReportPlan | null,
  ): string => {
    if (id === 'goal') {
      return userPrompt
        ? 'Confirmed user report request.'
        : 'Awaiting user report request...';
    }
    if (id === 'sources') {
      return attachments.length > 0
        ? `Attached ${attachments.length} datasets.`
        : 'Pending data validation selection...';
    }
    if (!currentPlan) return 'Processing workflow state...';

    switch (id) {
      case 'fields':
        return `Mapped ${currentPlan.columns.length} schema column targets.`;
      case 'preview':
        return 'Report generated successfully.';
      default:
        return '';
    }
  };

  const renderStepDetail = (
    id: StepId,
    currentPlan: ReportPlan | null,
    isLoaded: boolean,
    rows: any[],
  ): ReactNode => {
    if (id === 'sources' && attachments.length > 0) {
      return (
        <ul className='rb-step-list'>
          {attachments.map((a) => (
            <li key={a.id} style={{ color: 'var(--rb-fg)' }}>
              <span style={{ fontWeight: 600, color: 'var(--rb-primary)' }}>
                {a.categoryLabel}:
              </span>{' '}
              {a.file}
            </li>
          ))}
        </ul>
      );
    }
    if (!currentPlan) return null;

    switch (id) {
      case 'fields':
        return (
          <ul className='rb-step-list'>
            {currentPlan.columns.slice(0, 6).map((c) => (
              <li key={c}>{c}</li>
            ))}
            {currentPlan.columns.length > 6 && (
              <li className='rb-step-more'>
                + {currentPlan.columns.length - 6} more
              </li>
            )}
          </ul>
        );
      case 'preview':
        return isLoaded ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.625rem',
            }}
          >
            <ul className='rb-step-list'>
              <li>Materialized {rows.length} rows into the report table.</li>
            </ul>
            <button
              type='button'
              className='rb-btn rb-btn-outline'
              style={{
                fontSize: '0.75rem',
                padding: '0.3rem 0.75rem',
                width: 'fit-content',
              }}
              onClick={() =>
                previewTableRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                })
              }
            >
              <IconEye style={{ width: 12, height: 12 }} /> View Report
            </button>
          </div>
        ) : null;
      default:
        return null;
    }
  };

  // ─── Leave-the-page guard: styled confirm for in-app nav (<Prompt>) plus a
  // native beforeunload prompt for tab-close / hard refresh. Mirrors the pattern
  // used by EARChatUI / ChatUI elsewhere in the app. ───
  useEffect(() => {
    if (!shouldWarnOnLeave) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [shouldWarnOnLeave]);

  useEffect(() => {
    if (!isLeaveModalOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsLeaveModalOpen(false);
        setPendingLocation(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLeaveModalOpen]);

  const continueToPendingLocation = useCallback(() => {
    const target = pendingLocation;
    const action = pendingAction;
    // Clear the stashed transition BEFORE navigating. history.push/replace/goBack
    // can unmount this component synchronously, so any setState scheduled after
    // the navigation call would land on an unmounted component (a no-op that logs
    // a warning). The timeout below only mutates a ref, which is safe post-unmount.
    setPendingLocation(null);
    allowNavigationRef.current = true;
    // Resume with the ORIGINAL action so Back/Forward (POP) and replace
    // transitions complete as themselves. A blanket history.push would turn a POP
    // into a brand-new forward entry — stranding the user on the prior page and
    // re-arming this guard when they press Back again. goBack() completes the
    // common single-step Back that triggered the prompt.
    if (action === 'POP') {
      history.goBack();
    } else if (action === 'REPLACE' && target) {
      history.replace(target);
    } else if (target) {
      history.push(target);
    }
    // Only reset the bypass flag if we're still mounted (e.g. the transition was
    // itself re-blocked). setState is intentionally NOT called here.
    window.setTimeout(() => {
      allowNavigationRef.current = false;
    }, 0);
  }, [history, pendingLocation, pendingAction]);

  // <Prompt> message callback: block the nav, stash the target + its original
  // action, and open the modal.
  const handleBlockedNavigation = (
    nextLocation: any,
    action: 'PUSH' | 'REPLACE' | 'POP',
  ): boolean => {
    if (allowNavigationRef.current || !shouldWarnOnLeave) return true;
    setPendingLocation(nextLocation);
    setPendingAction(action);
    setIsLeaveModalOpen(true);
    return false;
  };

  const handleConfirmLeave = () => {
    setIsLeaveModalOpen(false);
    continueToPendingLocation();
  };

  const handleStayOnPage = () => {
    setIsLeaveModalOpen(false);
    setPendingLocation(null);
  };

  return (
    <div
      className={`standard-workflow-page${
        embedded ? ' standard-workflow-page--embedded' : ''
      } rb-root`}
    >
      <Prompt when={shouldWarnOnLeave} message={handleBlockedNavigation} />
      {sourceRun && !embedded && (
        <div style={{ marginBottom: '-20px' }}>
          <ToolBreadcrumb
            items={[
              SOURCE_RUN_ROOT[sourceRun.type],
              {
                label: sourceRun.label,
                to: SOURCE_RUN_DETAIL_PATH[sourceRun.type](sourceRun.runId),
              },
              { label: 'Report Builder' },
            ]}
          />
        </div>
      )}
      <WorkflowPageHeader
        title='Report Builder'
        description='Describe your reporting goal and select the governed data needed to build it.'
        newActionLabel='New Report'
        onNew={handleResetChat}
        disableNew={
          !userHasAccess ||
          (!hasConversation && !previewLoaded && !chatkitThinking)
        }
      />
      <WorkflowNotice>
        <p>
          BITE Data summarizes user-provided data and publicly available
          regulatory information for informational and planning purposes only.
          It is not a licensed U.S. Customs Broker and does not provide customs
          brokerage or legal advice.
        </p>
        <p>
          You remain responsible for reviewing all reports, calculations and
          AI-generated insights, and for making classification, valuation,
          compliance and filing decisions.
        </p>
      </WorkflowNotice>
      {!userHasAccess && (
        <div className='rb-license-gate'>
          <RequiredLicense
            userLicense={normalizedLicense}
            minimumLicense='business'
          />
        </div>
      )}
      <div ref={workflowMainRef} className='rb-workflow-main'>
        <div className='standard-workflow-assistant-card rb-card rb-assistant-card'>
          <WorkflowAssistantHeader
            title='Report Assistant'
            status={chatkitError ? 'error' : chatkitThinking ? 'running' : 'idle'}
          />
          <div className='rb-assistant-body'>
            {chatkitError && (
              <div className='rb-chatkit-error'>
                <IconAlertTriangle style={{ width: 16, height: 16 }} />
                <span>{chatkitError}</span>
              </div>
            )}

            <div className='rb-chatkit-shell'>
              <ReportChatPanel
                key={`rb-chat-session-${sessionResetNonce}`}
                ref={chatPanelRef}
                attachments={attachments}
                setChatkitError={setChatkitError}
                setActiveThreadId={setActiveThreadId}
                setChatkitThinking={setChatkitThinking}
                setPlan={setPlan}
                setPreviewRows={setPreviewRows}
                setPreviewLoaded={setPreviewLoaded}
                setUserPrompt={setUserPrompt}
                setProposedSources={setProposedSources}
                onReportReady={recordCompletedReport}
                teamId={selectedTeamId}
                readOnly={!userHasAccess}
              />
            </div>

            {attachments.length > 0 && (
              <div
                className='rb-attach-tray rb-composer-tray'
                style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem' }}
              >
                {attachments.map((a) => (
                  <span
                    key={`${a.category}-${a.file}`}
                    className='rb-attach-pill'
                  >
                    <IconReceipt style={{ width: 11, height: 11 }} />
                    <span className='rb-attach-cat'>{a.categoryLabel}:</span>
                    <span className='rb-attach-file'>{a.file}</span>
                    <button
                      type='button'
                      className='rb-attach-x'
                      onClick={() => removeAttachment(a)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {plan && (
        <div className='rb-report-section'>
          {/* Full-width page-break divider between the chat/progress row and the report */}
          <div className='rb-report-divider' />
          <div className='rb-card' ref={previewTableRef}>
            <div className='rb-header' style={{ marginBottom: '0.75rem' }}>
              <div>
                <div className='rb-eyebrow'>
                  <IconEye style={{ width: 12, height: 12 }} /> Report Preview
                  {plan.mode === 'comparison' && plan.programLabel
                    ? ` · ${plan.programLabel}`
                    : ''}
                </div>
                <div className='rb-card-title-lg'>
                  {previewLoaded
                    ? `${previewRows.length} rows returned`
                    : 'Processing database blocks...'}
                </div>
                {sourceDatasetsLabel && (
                  <div className='rb-source-strip'>
                    Source: {sourceDatasetsLabel}
                  </div>
                )}
              </div>
              {previewLoaded && previewRows.length > 0 && (
                <button
                  className='rb-btn rb-btn-primary'
                  onClick={handleDownloadCSV}
                >
                  <IconDownload /> Export CSV
                </button>
              )}
            </div>

            {previewLoaded ? (
              <>
                <div className='rb-table-wrap'>
                  <table className='rb-table'>
                    <thead>
                      <tr>
                        {plan.columns.map((colHeader) => (
                          <th
                            key={colHeader}
                            style={{
                              position: 'sticky',
                              top: 0,
                              zIndex: 2,
                              background: 'var(--rb-card-elev)',
                            }}
                          >
                            {humanizeHeader(colHeader)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row: any, i) => (
                        <tr key={i}>
                          {plan.columns.map((col, j) => {
                            let val = row[col];
                            if (val === undefined || val === null) {
                              val = row[col.toLowerCase().replace(/ /g, '_')];
                            }
                            return (
                              <td key={j}>
                                {formatCellDisplay(plan.mode, col, val)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {plan.mode === 'comparison' &&
                        previewRows.length > 0 &&
                        // Only when there's a dimension column to anchor "Total".
                        // A grand-total comparison (dimensions: []) is already a
                        // single total row, so a totals row would just duplicate it.
                        plan.columns.some(
                          (c) => !COMPARISON_METRIC_COLUMNS.has(c),
                        ) && (
                          <tr
                            className='rb-table-totals'
                            style={{ fontWeight: 700 }}
                          >
                            {plan.columns.map((col, j) => {
                              if (j === 0) return <td key={j}>Total</td>;
                              if (COMPARISON_DUTY_COLUMNS.has(col)) {
                                const sum = previewRows.reduce(
                                  (acc, r) => acc + (Number(r[col]) || 0),
                                  0,
                                );
                                return <td key={j}>{formatCurrency(sum)}</td>;
                              }
                              if (col === 'Delta %') {
                                const { overallPct: pct } =
                                  computeComparisonTotals(previewRows);
                                return (
                                  <td key={j}>
                                    {pct === null
                                      ? '—'
                                      : `${pct > 0 ? '+' : ''}${pct}%`}
                                  </td>
                                );
                              }
                              return <td key={j} />;
                            })}
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className='rb-empty'>
                Data mapping extraction execution pending...
              </div>
            )}
          </div>

          {previewLoaded && previewRows.length > 0 && (
            <div className='rb-card'>
              <div className='rb-header' style={{ marginBottom: '1rem' }}>
                <div>
                  <div className='rb-eyebrow'>
                    <IconReceipt style={{ width: 12, height: 12 }} /> Calculated
                    Metrics
                  </div>
                  <div className='rb-card-title-lg'>{plan.name}</div>
                </div>
              </div>

              {/* Live KPIs computed from the returned rows. */}
              {plan.mode === 'comparison'
                ? (() => {
                    const {
                      totalHistorical,
                      totalProjected,
                      netChange,
                      overallPct,
                    } = computeComparisonTotals(previewRows);
                    // Biggest mover is only meaningful when there's a dimension
                    // (country/program) to name — a grand-total comparison is one row.
                    const dimCols = plan.columns.filter(
                      (c) => !COMPARISON_METRIC_COLUMNS.has(c),
                    );
                    const mover =
                      dimCols.length > 0
                        ? previewRows.reduce(
                            (best: any, r: any) =>
                              best === null ||
                              (Number(r.Delta) || 0) > (Number(best.Delta) || 0)
                                ? r
                                : best,
                            null as any,
                          )
                        : null;
                    const tiles: {
                      label: string;
                      value: string;
                    }[] = [
                      {
                        label: 'Total Historical Duty',
                        value: formatCurrency(totalHistorical),
                      },
                      {
                        label: 'Total Projected Duty',
                        value: formatCurrency(totalProjected),
                      },
                      {
                        label: 'Net Change',
                        value: `${netChange > 0 ? '+' : ''}${formatCurrency(
                          netChange,
                        )}`,
                      },
                      {
                        label: 'Overall Change',
                        value:
                          overallPct === null
                            ? '—'
                            : `${overallPct > 0 ? '+' : ''}${overallPct}%`,
                      },
                    ];
                    // "Biggest Increase" only makes sense when some row's duty actually rose.
                    // mover is the max-Delta row; if even that is <= 0 (every row flat or
                    // declined), omit the tile rather than mislabel the least-negative row.
                    const moverDelta = mover ? Number(mover.Delta) || 0 : 0;
                    if (mover && moverDelta > 0) {
                      const moverLabel = dimCols
                        .map((c) => mover[c])
                        .filter(Boolean)
                        .join(' · ');
                      tiles.push({
                        label: 'Biggest Increase',
                        value: `${moverLabel || '—'} (+${formatCurrency(
                          moverDelta,
                        )})`,
                      });
                    }
                    return (
                      <div className='rb-kpi-grid'>
                        {tiles.map((t, i) => (
                          <div className='rb-kpi-card' key={i}>
                            <div className='rb-kpi-label'>{t.label}</div>
                            <div className='rb-kpi-value'>{t.value}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                : (() => {
                    // Normal report: row count + sum of each numeric, non-identifier column.
                    const numericCols = plan.columns.filter((c) => {
                      if (isIdentifierColumn(c)) return false;
                      return previewRows.some((r) => {
                        if (r[c] === null || r[c] === undefined || r[c] === '')
                          return false;
                        return !Number.isNaN(Number(r[c]));
                      });
                    });
                    const tiles: { label: string; value: string }[] = [
                      { label: 'Rows', value: `${previewRows.length}` },
                      ...numericCols.map((c) => {
                        const sum = previewRows.reduce(
                          (acc, r) => acc + (Number(r[c]) || 0),
                          0,
                        );
                        return {
                          label: `Total ${humanizeHeader(c)}`,
                          value: `${Math.round(sum * 1000) / 1000}`,
                        };
                      }),
                    ];
                    return (
                      <div className='rb-kpi-grid'>
                        {tiles.map((t, i) => (
                          <div className='rb-kpi-card' key={i}>
                            <div className='rb-kpi-label'>{t.label}</div>
                            <div className='rb-kpi-value'>{t.value}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

              {/* AI highlights, when the backend provides them. */}
              {plan.insights && plan.insights.length > 0 && (
                <div className='rb-section' style={{ marginTop: '1rem' }}>
                  <div className='rb-section-head'>
                    <IconSparkles
                      style={{
                        width: 14,
                        height: 14,
                        color: 'var(--rb-primary)',
                      }}
                    />
                    <span>AI Extracted Highlights</span>
                  </div>
                  <div
                    style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}
                  >
                    {plan.insights.map((insight, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: 'var(--rb-surface-dark)',
                          padding: '1rem',
                          borderRadius: '8px',
                          border: '1px solid var(--rb-border)',
                          flex: '1 1 200px',
                        }}
                      >
                        <div
                          style={{
                            color: 'var(--rb-primary)',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                          }}
                        >
                          {insight}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── RIGHT WORKFLOW TRACKER ─── */}
      <div className='rb-progress'>
        <div className='standard-workflow-progress-card rb-card rb-progress-card'>
          <WorkflowProgressHeader title='Report Progress' badge='Beta' />
          <div className='standard-workflow-timeline'>
            {STEPS.map((step, index) => {
              const status = stepStatuses[step.id];
              const open = openStep === step.id;
              const workflowStatus =
                status === 'done'
                  ? 'complete'
                  : status === 'active'
                  ? 'running'
                  : 'pending';
              return (
                <div
                  key={step.id}
                  className={`standard-workflow-step ${workflowStatus}`}
                >
                  <div
                    className='standard-workflow-step-marker'
                    aria-hidden='true'
                  >
                    <span />
                    {index < STEPS.length - 1 && <i />}
                  </div>
                  <div
                    className={`standard-workflow-step-card-shell${
                      open ? ' expanded' : ''
                    }`}
                  >
                    <button
                      className='standard-workflow-step-summary'
                      onClick={() => toggleStep(step.id)}
                      type='button'
                      aria-expanded={open}
                    >
                      <div className='standard-workflow-step-heading'>
                        <span>{step.title}</span>
                        <span className='standard-workflow-step-actions'>
                          <strong>
                            {status === 'done'
                              ? 'Complete'
                              : status === 'active'
                              ? 'In progress'
                              : 'Pending'}
                          </strong>
                          <svg
                            className='standard-workflow-step-chevron'
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                            fill='none'
                            aria-hidden='true'
                          >
                            <path
                              d={open ? 'M4 6L8 10L12 6' : 'M10 12L6 8L10 4'}
                              stroke='currentColor'
                              strokeWidth='1.5'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </span>
                      </div>
                      <p>{getStepSummary(step.id, plan)}</p>
                    </button>
                    <div
                      className='standard-workflow-step-details custom-body'
                      aria-hidden={!open}
                    >
                      <div className='rb-step-body'>
                      {!(
                        step.id === 'sources' &&
                        proposedSources.length > 0 &&
                        !plan &&
                        attachments.length === 0
                      ) && (
                        <>
                          {renderStepDetail(
                            step.id,
                            plan,
                            previewLoaded,
                            previewRows,
                          )}
                        </>
                      )}

                      {step.id === 'sources' &&
                        proposedSources.length > 0 &&
                        !plan &&
                        attachments.length === 0 && (
                          <div className='rb-rec-picker'>
                            <div className='rb-step-summary'>
                              Select the relevant catalog discoveries to attach:
                            </div>
                            <div className='rb-rec-search-wrap'>
                              <input
                                type='text'
                                placeholder='Filter list...'
                                value={discoverySearchTerm}
                                onChange={(e) =>
                                  setDiscoverySearchTerm(e.target.value)
                                }
                                className='rb-rec-search'
                              />
                            </div>
                            <div className='rb-rec-filters'>
                              {(
                                ['ALL', 'TARIFF', 'ACE', 'PRODUCT'] as const
                              ).map((tab) => (
                                <button
                                  key={tab}
                                  type='button'
                                  onClick={() => setDiscoveryFilterType(tab)}
                                  className={`rb-rec-filter ${
                                    discoveryFilterType === tab
                                      ? 'is-active'
                                      : ''
                                  }`}
                                >
                                  {tab}
                                </button>
                              ))}
                            </div>
                            <div
                              className='rb-rec-files'
                              style={{ maxHeight: '18rem', overflowY: 'auto' }}
                            >
                              {proposedSources
                                .filter(
                                  (s) =>
                                    discoveryFilterType === 'ALL' ||
                                    s.type?.toUpperCase() ===
                                      discoveryFilterType,
                                )
                                .filter((s) =>
                                  s.name
                                    ?.toLowerCase()
                                    .includes(
                                      discoverySearchTerm.toLowerCase(),
                                    ),
                                )
                                .map((source) => {
                                  const isChecked =
                                    selectedDiscoveredIds.includes(source.id);
                                  const isAce =
                                    source.type?.toUpperCase() === 'ACE';
                                  const isProduct =
                                    source.type?.toUpperCase() === 'PRODUCT';
                                  return (
                                    <button
                                      key={source.id}
                                      type='button'
                                      className={`rb-rec-file ${
                                        isChecked ? 'is-attached' : ''
                                      }`}
                                      onClick={() =>
                                        setSelectedDiscoveredIds((prev) =>
                                          prev.includes(source.id)
                                            ? prev.filter(
                                                (id) => id !== source.id,
                                              )
                                            : [...prev, source.id],
                                        )
                                      }
                                    >
                                      {isChecked ? (
                                        <IconCheck
                                          style={{ width: 11, height: 11 }}
                                        />
                                      ) : isAce ? (
                                        <IconShieldCheck
                                          style={{ width: 11, height: 11 }}
                                        />
                                      ) : isProduct ? (
                                        <IconDatabase
                                          style={{ width: 11, height: 11 }}
                                        />
                                      ) : (
                                        <IconReceipt
                                          style={{ width: 11, height: 11 }}
                                        />
                                      )}
                                      <span className='rb-rec-file-name'>
                                        {source.name}
                                      </span>
                                      <span className='rb-rec-file-cat'>
                                        {isAce
                                          ? 'ACE'
                                          : isProduct
                                          ? 'PRODUCT'
                                          : 'TARIFF'}
                                      </span>
                                    </button>
                                  );
                                })}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: '0.5rem',
                              }}
                            >
                              <button
                                type='button'
                                disabled={
                                  selectedDiscoveredIds.length === 0 ||
                                  chatkitThinking ||
                                  !userHasAccess
                                }
                                className='rb-btn rb-btn-primary'
                                onClick={() => {
                                  const chosen = proposedSources.filter((s) =>
                                    selectedDiscoveredIds.includes(s.id),
                                  );
                                  // Build the new attachment list synchronously so it can
                                  // be passed directly to sendMessage — avoids depending on
                                  // React state having flushed before the SSE turn fires.
                                  const newAttachments: Attachment[] =
                                    chosen.map((s) => ({
                                      category:
                                        s.type?.toLowerCase() === 'ace'
                                          ? 'ace'
                                          : s.type?.toLowerCase() === 'product'
                                          ? 'product'
                                          : 'tariff',
                                      categoryLabel:
                                        s.type?.toUpperCase() === 'ACE'
                                          ? 'ACE'
                                          : s.type?.toUpperCase() === 'PRODUCT'
                                          ? 'PRODUCT'
                                          : 'TARIFF',
                                      id: s.id,
                                      file: s.name,
                                    }));
                                  const mergedAttachments = [
                                    ...attachments,
                                    ...newAttachments,
                                  ];
                                  setAttachments(mergedAttachments);
                                  // Markdown so the chat bubble (rendered via ReactMarkdown)
                                  // shows a real bulleted list with spacing — single "\n"
                                  // collapses to a space, so use "- " items + blank lines.
                                  const payloadStr = chosen
                                    .map((s) => `- ${s.name}`)
                                    .join('\n');
                                  chatPanelRef.current?.sendMessage(
                                    `I've attached these datasets:\n\n${payloadStr}\n\nPlease compile this report request:\n\n"${userPrompt}"`,
                                    mergedAttachments,
                                  );
                                  setProposedSources([]);
                                  setSelectedDiscoveredIds([]);
                                  setDiscoverySearchTerm('');
                                }}
                              >
                                Confirm Attachment (
                                {selectedDiscoveredIds.length})
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {!plan && (
        <div className='rb-placeholder-section'>
          <div className='rb-card rb-placeholder'>
            <div className='rb-placeholder-icon'>
              <IconTable style={{ width: 22, height: 22 }} />
            </div>
            <div className='rb-placeholder-title'>No active report</div>
            <div className='rb-placeholder-sub'>
              Describe your requirements to generate tables and calculations.
            </div>
          </div>
        </div>
      )}

      <Dialog
        className='dialog_box'
        open={isLeaveModalOpen}
        onClose={handleStayOnPage}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '620px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          Leave the Report Builder?
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={handleStayOnPage}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              Leaving this page will discard the current workflow and its
              results. Make sure you export the CSV first if you want to keep
              them.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button className='btn-new-clear' onClick={handleStayOnPage}>
                <div>Stay on Page</div>
              </button>
              <button className='btn-new-automate' onClick={handleConfirmLeave}>
                <div>Leave Anyway</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ─── SUBSYSTEM: SELF-CONTAINED SSE CHAT PANEL ─── */

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

interface CustomPanelProps {
  attachments: Attachment[];
  setChatkitError: (s: string | null) => void;
  setActiveThreadId: (s: string | null) => void;
  setChatkitThinking: (b: boolean) => void;
  setPlan: React.Dispatch<React.SetStateAction<ReportPlan | null>>;
  setPreviewRows: React.Dispatch<React.SetStateAction<any[]>>;
  setPreviewLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setUserPrompt: (s: string) => void;
  setProposedSources: (s: any[]) => void;
  onReportReady: (args: {
    requestId: string;
    title: string;
    sources: Attachment[];
    metadata?: Record<string, unknown>;
    columns: string[];
    rows: any[];
    mode?: 'comparison';
  }) => Promise<string | null>;
  teamId?: string;
  // When true, the tool is license-locked → the composer/prompts are disabled (view-only).
  readOnly?: boolean;
}

// Workflow stages we can infer from the SSE events the chat panel already parses.
// Drives the phase-aware status messages shown while the assistant is working.
type StreamPhase =
  | 'thinking'
  | 'sources'
  | 'handoff'
  | 'handoff_compare'
  | 'handoff_product'
  | 'generating';

// User-facing only — never reference internal components (e.g. "schema-compiler") or
// imply named sub-agents; describe the work plainly.
const THINKING_MESSAGES: Record<StreamPhase, string[]> = {
  thinking: [
    'Understanding your request…',
    'Interpreting your reporting goal…',
    'Planning your report…',
  ],
  sources: [
    'Scanning your data catalog…',
    'Reviewing data sources…',
    'Matching governed datasets…',
    'Selecting the best sources…',
  ],
  // Single-dataset compilation: mapping the dataset schema before any query runs.
  handoff: [
    'Reviewing your dataset…',
    'Mapping your dataset schema…',
    'Identifying the relevant duty columns…',
    'Validating column mappings…',
    'Analyzing your dataset…',
    'Preparing your query…',
  ],
  // Single product-database compilation: reviewing the product catalog and mapping its
  // classification / compliance / duty fields before any query runs.
  handoff_product: [
    'Reviewing your product database…',
    'Mapping your product catalog…',
    'Identifying the relevant product fields…',
    'Reviewing classification and HTS data…',
    'Checking compliance and screening status…',
    'Analyzing product duty and landed-cost figures…',
    'Preparing your query…',
  ],
  // Cross-dataset comparison — the longest stage, so a richer ACE/tariff-flavored
  // rotation keeps it from feeling repetitive. Switches to 'generating' once the query
  // begins executing (driven by the backend status event).
  handoff_compare: [
    'Reviewing both datasets…',
    'Mapping your historical ACE duties…',
    'Projecting your 2026 tariff costs…',
    'Aligning historical vs projected duties…',
    'Matching HTS codes across both datasets…',
    'Identifying the relevant duty columns…',
    'Reconciling the two schemas…',
    'Preparing your duty comparison…',
  ],
  // Query is executing / results are in: building and finalizing the report output.
  generating: [
    'Consolidating your data…',
    'Building your report…',
    'Computing the duty delta…',
    'Finalizing your report…',
    'Almost there…',
  ],
};

// ChatGPT-style cycling status indicator shown in place of an empty, still-streaming
// assistant bubble. Reuses the existing .rb-msg-typing bouncing dots; the keyed text
// span remounts each tick so the pure-CSS fade animation re-runs per message.
const ReportThinkingIndicator: React.FC<{ phase: StreamPhase }> = ({
  phase,
}) => {
  const msgs = THINKING_MESSAGES[phase] ?? THINKING_MESSAGES.thinking;
  const [i, setI] = React.useState(0);
  const messageIndex = i % msgs.length;
  const message = msgs[messageIndex] ?? THINKING_MESSAGES.thinking[0];

  React.useEffect(() => {
    setI(0);
  }, [phase]);

  React.useEffect(() => {
    // Slower cadence so longer stages (esp. the comparison handoff) don't feel repetitive.
    const id = setInterval(() => setI((p) => (p + 1) % msgs.length), 3200);
    return () => clearInterval(id);
  }, [msgs.length]);

  return (
    <div className='rb-thinking' role='status' aria-live='polite'>
      <div className='rb-thinking-title'>
        <span aria-hidden='true' />
        Thinking
      </div>
      <span key={`${phase}-${messageIndex}`} className='rb-thinking-text'>
        {message.replace(/…$/, '')}
      </span>
    </div>
  );
};

const ReportChatPanel = React.forwardRef<ChatPanelHandle, CustomPanelProps>(
  (
    {
      attachments,
      setChatkitError,
      setActiveThreadId,
      setChatkitThinking,
      setPlan,
      setPreviewRows,
      setPreviewLoaded,
      setUserPrompt,
      setProposedSources,
      onReportReady,
      teamId,
      readOnly,
    },
    ref,
  ) => {
    const [messages, setMessages] = React.useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = React.useState('');
    const [isStreaming, setIsStreaming] = React.useState(false);
    const [streamPhase, setStreamPhase] =
      React.useState<StreamPhase>('thinking');
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const abortRef = React.useRef<AbortController | null>(null);
    const prevMsgCountRef = React.useRef(0);
    const wasStreamingRef = React.useRef(false);

    React.useEffect(() => {
      return () => {
        abortRef.current?.abort();
      };
    }, []);

    // Scroll to the bottom when a genuinely new bubble is added, or when a
    // reply that was streaming token-by-token into an existing bubble just
    // finished — never while it's still going, so following the thread
    // never fights someone who scrolled up to re-read an earlier message.
    // Without the `isStreaming` edge, a long reply that streamed past the
    // bottom of the last scroll would have nothing left to bring it back
    // into view once it finished.
    React.useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;

      const isNewMessage = messages.length !== prevMsgCountRef.current;
      prevMsgCountRef.current = messages.length;

      const justFinishedStreaming = wasStreamingRef.current && !isStreaming;
      wasStreamingRef.current = isStreaming;

      if (!isNewMessage && !justFinishedStreaming) return;

      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [messages, isStreaming]);

    const executeSseAgentTurn = React.useCallback(
      async (messageText: string, syncDatasets?: Attachment[]) => {
        if (!messageText.trim() || isStreaming) return;

        const userMsgId = `user_${Date.now()}`;

        // Only the user message is added up front. Assistant bubbles are created
        // per-turn keyed by the backend item_id as deltas arrive, and the trailing
        // thinking indicator covers the wait before/between those messages.
        setMessages((prev) => [
          ...prev,
          { id: userMsgId, role: 'user', text: messageText },
        ]);
        setIsStreaming(true);
        setChatkitThinking(true);
        setChatkitError(null);
        setUserPrompt(messageText);
        setActiveThreadId(userMsgId);
        // Clear any results from a previous compilation turn so stale rows and
        // columns don't bleed into the new report.
        setPreviewRows([]);
        setPlan(null);
        setPreviewLoaded(false);

        // Cancel any prior in-flight request and create a fresh controller
        // whose signal is threaded through to the fetch call.
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        // Use the caller-supplied dataset list when provided so we don't
        // depend on React state having flushed before this fires.
        const datasetsForRequest = syncDatasets ?? attachments;

        // A turn with datasets attached starts with the specialist handoff/schema
        // mapping; a turn without is source discovery. A dual ACE+TARIFF attachment is a
        // comparison, which runs both specialists (the longest stage) → richer messages.
        // Refined as SSE events arrive (recommendations → 'sources', query → 'generating').
        const cats = new Set(datasetsForRequest.map((d) => d.category));
        // ACE+TARIFF is the cross-module comparison; a product database (standalone) gets
        // its own product-flavored rotation; anything else uses the generic single-dataset set.
        const handoffPhase: StreamPhase =
          cats.has('ace') && cats.has('tariff')
            ? 'handoff_compare'
            : cats.has('product')
            ? 'handoff_product'
            : 'handoff';
        setStreamPhase(
          datasetsForRequest.length > 0 ? handoffPhase : 'thinking',
        );

        // Buffer query results and reveal them only once the stream completes, so the
        // preview table and the Stage 3 "done" state land together with the agent's
        // closing "report is ready" message — not seconds before it.
        const collectedRows: any[] = [];
        let collectedCols: string[] = [];
        let comparisonMode = false;
        let comparisonProgram = 'total';
        let comparisonProgramLabel = 'Total';
        let hasResult = false;

        try {
          const response = await ReportsAPI.createSession(
            { message: messageText, sync_datasets: datasetsForRequest },
            teamId || '',
            controller.signal,
          );

          if (!response.ok) throw new Error(`Server error: ${response.status}`);
          if (!response.body)
            throw new Error('Network streaming response is unreadable.');

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            if (controller.signal.aborted) break;
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() ?? '';

            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              const json = line.slice(6).trim();
              if (!json) continue;

              try {
                const payload = JSON.parse(json);

                if (
                  payload.type === 'thread.item.delta' &&
                  payload.delta?.content
                ) {
                  // Group by the backend item_id: append to the matching bubble if it
                  // exists (token streaming within a turn), otherwise start a new one
                  // (a new agent turn → a separate message bubble).
                  const itemId: string = payload.item_id || 'asst_pending';
                  setMessages((prev) => {
                    const idx = prev.findIndex((m) => m.id === itemId);
                    if (idx !== -1) {
                      const next = [...prev];
                      next[idx] = {
                        ...next[idx],
                        text: next[idx].text + payload.delta.content,
                      };
                      return next;
                    }
                    return [
                      ...prev,
                      {
                        id: itemId,
                        role: 'assistant',
                        text: payload.delta.content,
                      },
                    ];
                  });
                }

                // Backend status signal — fires the moment query execution begins, so the
                // carousel leaves the mapping/handoff messages instead of leaking stale
                // "mapping your duties…" lines while the report is actually being built.
                // Guard against unknown phase values: THINKING_MESSAGES[phase] would be
                // undefined for any value not in StreamPhase, crashing ReportThinkingIndicator.
                if (payload.type === 'custom.status' && payload.phase) {
                  const VALID_STREAM_PHASES = new Set<string>([
                    'thinking',
                    'sources',
                    'handoff',
                    'handoff_compare',
                    'handoff_product',
                    'generating',
                  ]);
                  if (VALID_STREAM_PHASES.has(payload.phase)) {
                    setStreamPhase(payload.phase as StreamPhase);
                  }
                }

                // These events require the backend controller to emit them explicitly.
                // The SSE parser handles them here so they work as soon as backend support is added.
                if (payload.type === 'custom.recommendations') {
                  // Ignore stray recommendations on a compilation turn (datasets already
                  // attached). Otherwise the handoff phase gets knocked back to 'sources'
                  // and the already-confirmed Stage 2 picker can reappear.
                  if (datasetsForRequest.length === 0) {
                    setStreamPhase('sources');
                    setProposedSources(payload.data ?? []);
                  }
                }
                if (payload.type === 'custom.query_result') {
                  setStreamPhase('generating');
                  const rows = payload.rows ?? [];
                  const isComparison = payload.mode === 'comparison';
                  hasResult = true;
                  if (isComparison) {
                    // Comparison results are a single authoritative set — replace, and
                    // trust the backend's explicit, ordered column list.
                    collectedRows.length = 0;
                    collectedRows.push(...rows);
                    collectedCols = payload.columns ?? [];
                    comparisonMode = true;
                    comparisonProgram = payload.program ?? 'total';
                    comparisonProgramLabel = payload.programLabel ?? 'Total';
                  } else {
                    // Generic results may arrive in multiple frames — keep appending,
                    // and union the row keys (mixed-schema safe).
                    collectedRows.push(...rows);
                    const cols = Array.from(
                      new Set<string>(rows.flatMap((r: any) => Object.keys(r))),
                    );
                    collectedCols = Array.from(
                      new Set<string>([...collectedCols, ...cols]),
                    );
                  }
                }

                if (payload.type === 'error') {
                  setChatkitError(payload.message || 'Stream error.');
                }
              } catch {
                // Ignore partial / malformed SSE frames
              }
            }
          }
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            setChatkitError(err.message || 'Stream connection failed.');
          }
        } finally {
          // Reveal the buffered result now the stream is complete: the table, the
          // Stage 3 "done" state, and the closing message all surface together. Skip
          // if the user aborted mid-stream or no result ever arrived.
          if (hasResult && !controller.signal.aborted) {
            const reportName = comparisonMode ? 'Duty Comparison' : 'Report Results';
            setPreviewRows(collectedRows);
            setPlan({
              name: reportName,
              question: '',
              sources: [],
              columns: collectedCols,
              filters: [],
              groupings: [],
              metrics: [],
              warnings: [],
              assumptions: [],
              insights: [],
              mode: comparisonMode ? 'comparison' : undefined,
              program: comparisonMode ? comparisonProgram : undefined,
              programLabel: comparisonMode ? comparisonProgramLabel : undefined,
            });
            setPreviewLoaded(true);
            await onReportReady({
              requestId: userMsgId,
              title: reportName,
              sources: datasetsForRequest,
              metadata: {
                mode: comparisonMode ? 'comparison' : null,
                program: comparisonMode ? comparisonProgram : null,
                rowCount: collectedRows.length,
              },
              columns: collectedCols,
              rows: collectedRows,
              mode: comparisonMode ? 'comparison' : undefined,
            });
          }
          setIsStreaming(false);
          setChatkitThinking(false);
        }
      },
      // attachments and teamId are captured per-send; isStreaming gates concurrent calls
      [attachments, teamId, isStreaming, onReportReady],
    );

    // Expose sendMessage so the parent can trigger a turn programmatically (e.g. dataset confirm).
    // The optional syncDatasets arg bypasses the stale attachments closure.
    React.useImperativeHandle(
      ref,
      () => ({
        sendMessage: (text: string, syncDatasets?: Attachment[]) =>
          executeSseAgentTurn(text, syncDatasets),
      }),
      [executeSseAgentTurn],
    );

    const handleSubmit = (e?: React.FormEvent) => {
      e?.preventDefault();
      const trimmed = inputValue.trim();
      if (!trimmed || isStreaming) return;
      setInputValue('');
      executeSseAgentTurn(trimmed);
    };

    return (
      <div
        className='rb-chatkit'
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        {/* Empty state: suggested prompts + composer centered together */}
        {messages.length === 0 ? (
          <div className='rb-chat-start'>
            <div className='rb-chat-prompts'>
              {SUGGESTED_PROMPTS.map((p) => (
                <button
                  key={p}
                  type='button'
                  className='rb-chat-prompt-btn'
                  onClick={() => executeSseAgentTurn(p)}
                  disabled={isStreaming || readOnly}
                >
                  <IconSparkles style={{ width: 11, height: 11 }} /> {p}
                </button>
              ))}
            </div>

            <WorkflowComposer
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleSubmit}
              placeholder={
                readOnly
                  ? 'A Business license is required to use the Report Builder.'
                  : 'Describe the report you want to build...'
              }
              disabled={isStreaming || readOnly}
            />
          </div>
        ) : (
          <>
            {/* Scrollable message thread */}
            <div className='rb-chat-messages' ref={scrollRef}>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`rb-chat-msg rb-chat-msg--${msg.role} rb-chat-msg-enter`}
                >
                  <div className='rb-chat-msg-text'>
                    <ReactMarkdown components={CHAT_MD_COMPONENTS}>
                      {msg.text}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
              {/* Phase-aware status carousel — visible whenever the agent is working,
                  including the wait before the first reply and the gaps between
                  separate assistant messages. */}
              {isStreaming && (
                <div className='rb-chat-msg rb-chat-msg--assistant rb-chat-msg-enter rb-chat-msg--thinking'>
                  <ReportThinkingIndicator phase={streamPhase} />
                </div>
              )}
            </div>

            <WorkflowComposer
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleSubmit}
              placeholder={
                readOnly
                  ? 'A Business license is required to use the Report Builder.'
                  : 'Describe the report you want to build...'
              }
              disabled={isStreaming || readOnly}
            />
          </>
        )}
      </div>
    );
  },
);

ReportChatPanel.displayName = 'ReportChatPanel';

export default AIReportBuilder;
